import React, { useState, useMemo } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertTriangle, Search, Filter, Calendar, Download, ChevronDown, ChevronLeft,
  Handshake, UserPlus, Trophy, SkipForward, Briefcase, TrendingUp, TrendingDown,
  Truck, Newspaper, ShieldAlert, ArrowLeft, ExternalLink
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import DateRangeSelect from '@/components/DateRangeSelect';
import InfoTip from '@/components/InfoTip';
import { useBranding } from '@/context/BrandingContext';
import { createBrandedPDF, addSectionTitle, addMetricCards, addTable, addText, downloadPDF, checkNewPage } from '@/utils/pdfExport';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import QuickAssignModal from '@/components/QuickAssignModal';

/* Quick Actions dropdown */
const QuickActions = ({ mpn, bomRef, onAssign }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button size="sm" variant="outline" className="h-7 px-2 text-xs gap-1" data-testid={`action-${mpn}`}>
        Action <ChevronDown className="h-3 w-3" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-48">
      <DropdownMenuItem onClick={() => toast.success('Referred to 1Source')}>
        <Handshake className="h-3.5 w-3.5 mr-2 text-green-600" /> Refer to 1Source
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onAssign?.()}>
        <UserPlus className="h-3.5 w-3.5 mr-2 text-blue-600" /> Assign
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => toast.info('Report Win opened')}>
        <Trophy className="h-3.5 w-3.5 mr-2 text-amber-600" /> Report Win
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => toast.info('Skipped')}>
        <SkipForward className="h-3.5 w-3.5 mr-2 text-gray-500" /> Skip
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

/* Generate trend data */
function genTrend(base, pctChange) {
  return Array.from({ length: 30 }, (_, i) => ({
    day: i + 1,
    price: base + (base * pctChange / 100) * (i / 30) + (Math.random() - 0.5) * base * 0.03,
  }));
}

/* --- SUPPLIER DATA (Live BOMs only) --- */
const suppliersData = [
  {
    id: 1, name: 'DigiKey', region: 'USA', totalSourcing12m: 1250000, overallEfficiency: 82,
    hasRiskNews: true, riskNewsList: [
      { title: 'DigiKey experiencing delays on Asian-origin shipments', date: 'Feb 2026', severity: 'Medium' },
    ],
    components: [
      { mpn: 'STM32F407VGT6', bomRef: 'Main Controller Board v2.1', category: 'MCU', qty: 25000, unitPrice: 12.50, bestRate: 9.20, anchor: 10.80, efficiency: 74, efficacy: 68, trend: 5, trendData: genTrend(12.50, 5), risk: 'Low', supplySourcing: 312500 },
      { mpn: 'TPS54331DR', bomRef: 'Main Controller Board v2.1', category: 'Power IC', qty: 25000, unitPrice: 1.65, bestRate: 1.05, anchor: 1.35, efficiency: 82, efficacy: 79, trend: -5, trendData: genTrend(1.65, -5), risk: 'Low', supplySourcing: 41250 },
      { mpn: 'GRM188R71H104KA93D', bomRef: 'IoT Sensor Board v1.0', category: 'Capacitor', qty: 100000, unitPrice: 0.028, bestRate: 0.012, anchor: 0.018, efficiency: 64, efficacy: 58, trend: -3, trendData: genTrend(0.028, -3), risk: 'Low', supplySourcing: 2800 },
    ],
  },
  {
    id: 2, name: 'Mouser', region: 'USA', totalSourcing12m: 980000, overallEfficiency: 78,
    hasRiskNews: false, riskNewsList: [],
    components: [
      { mpn: 'ESP32-WROOM-32E', bomRef: 'IoT Sensor Board v1.0', category: 'Wireless IC', qty: 22000, unitPrice: 3.20, bestRate: 2.00, anchor: 2.60, efficiency: 81, efficacy: 75, trend: 0, trendData: genTrend(3.20, 0), risk: 'Low', supplySourcing: 70400 },
      { mpn: 'MT48LC16M16A2', bomRef: 'Main Controller Board v2.1', category: 'Memory IC', qty: 20000, unitPrice: 3.50, bestRate: 2.10, anchor: 2.80, efficiency: 60, efficacy: 55, trend: 18, trendData: genTrend(3.50, 18), risk: 'Medium', supplySourcing: 70000 },
    ],
  },
  {
    id: 3, name: 'Arrow Electronics', region: 'Global', totalSourcing12m: 720000, overallEfficiency: 75,
    hasRiskNews: true, riskNewsList: [
      { title: 'Arrow Electronics flagged for extended lead times on passives', date: 'Jan 2026', severity: 'Low' },
    ],
    components: [
      { mpn: 'USB-C-16P-SMD', bomRef: 'IoT Sensor Board v1.0', category: 'Connector', qty: 50000, unitPrice: 0.38, bestRate: 0.22, anchor: 0.30, efficiency: 79, efficacy: 72, trend: 12, trendData: genTrend(0.38, 12), risk: 'Low', supplySourcing: 19000 },
      { mpn: 'NRF52840-QIAA-R', bomRef: 'BLE Module v3.0', category: 'MCU', qty: 8000, unitPrice: 5.75, bestRate: 4.50, anchor: 5.00, efficiency: 78, efficacy: 70, trend: 12, trendData: genTrend(5.75, 12), risk: 'High', supplySourcing: 46000 },
    ],
  },
  {
    id: 4, name: 'Chip1Exchange', region: 'Asia', totalSourcing12m: 340000, overallEfficiency: 45,
    hasRiskNews: true, riskNewsList: [
      { title: 'Chip1Exchange under review for counterfeit part concerns', date: 'Mar 2026', severity: 'High' },
      { title: 'Slow RFQ responses reported by multiple buyers', date: 'Feb 2026', severity: 'Medium' },
    ],
    components: [
      { mpn: 'GRM188R71H104KA93D', bomRef: 'Main Controller Board v2.1', category: 'Capacitor', qty: 200000, unitPrice: 0.015, bestRate: 0.012, anchor: 0.018, efficiency: 125, efficacy: 92, trend: -3, trendData: genTrend(0.015, -3), risk: 'Medium', supplySourcing: 3000 },
    ],
  },
  {
    id: 5, name: 'Farnell', region: 'Europe', totalSourcing12m: 560000, overallEfficiency: 80,
    hasRiskNews: false, riskNewsList: [],
    components: [
      { mpn: 'TPS62130RGTR', bomRef: 'Power Supply Board v1.2', category: 'DC-DC', qty: 15000, unitPrice: 2.15, bestRate: 1.65, anchor: 1.90, efficiency: 88, efficacy: 82, trend: 0, trendData: genTrend(2.15, 0), risk: 'Low', supplySourcing: 32250 },
      { mpn: 'SN65HVD230DR', bomRef: 'CAN Bus Module v2.0', category: 'CAN Transceiver', qty: 12000, unitPrice: 0.95, bestRate: 0.65, anchor: 0.80, efficiency: 84, efficacy: 78, trend: -3, trendData: genTrend(0.95, -3), risk: 'Low', supplySourcing: 11400 },
    ],
  },
  {
    id: 6, name: 'LCSC Electronics', region: 'Asia', totalSourcing12m: 180000, overallEfficiency: 42,
    hasRiskNews: true, riskNewsList: [
      { title: 'LCSC pricing inconsistencies reported on MLCC components', date: 'Feb 2026', severity: 'Medium' },
    ],
    components: [
      { mpn: 'RC0402FR-07100KL', bomRef: 'IoT Sensor Board v1.0', category: 'Resistor', qty: 500000, unitPrice: 0.003, bestRate: 0.002, anchor: 0.0025, efficiency: 83, efficacy: 70, trend: 0, trendData: genTrend(0.003, 0), risk: 'Low', supplySourcing: 1500 },
    ],
  },
];

const effBadge = (v) => v >= 80 ? 'bg-green-100 text-green-800' : v >= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800';

const SupplierScorecard = () => {
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCtx, setAssignCtx] = useState({});
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [bomFilter, setBomFilter] = useState('all');
  const [dateRange, setDateRange] = useState('ltm');
  const [minValue, setMinValue] = useState('');

  const totalSourcing = suppliersData.reduce((s, sup) => s + sup.totalSourcing12m, 0);
  const suppliersWithRisk = suppliersData.filter(s => s.hasRiskNews).length;
  const lowEffThreshold = Math.max(100000, totalSourcing * 0.05);
  const suppliersLowEff = suppliersData.filter(s => s.overallEfficiency < 50 && s.totalSourcing12m >= lowEffThreshold).length;

  const allBoms = useMemo(() => [...new Set(suppliersData.flatMap(s => s.components.map(c => c.bomRef)))], []);
  const allRegions = useMemo(() => [...new Set(suppliersData.map(s => s.region))], []);

  const filtered = useMemo(() => {
    return suppliersData.filter(s => {
      const q = searchQuery.toLowerCase();
      if (q) {
        const nameMatch = s.name.toLowerCase().includes(q);
        const compMatch = s.components.some(c => c.mpn.toLowerCase().includes(q));
        if (!nameMatch && !compMatch) return false;
      }
      if (countryFilter !== 'all' && s.region !== countryFilter) return false;
      if (bomFilter !== 'all' && !s.components.some(c => c.bomRef === bomFilter)) return false;
      if (minValue && s.totalSourcing12m < Number(minValue)) return false;
      return true;
    });
  }, [searchQuery, countryFilter, bomFilter, minValue]);

  /* Negotiation Kit PDF */
  const generateNegotiationKit = (supplier) => {
    const doc = createBrandedPDF(`Negotiation Kit: ${supplier.name}`, 'Supplier Intelligence & Negotiation Brief', branding);
    let y = doc._startY;
    y = addSectionTitle(doc, 'Supplier Overview', y);
    y = addMetricCards(doc, [
      { label: 'Supplier', value: supplier.name },
      { label: 'Region', value: supplier.region },
      { label: 'Total Sourcing (12m)', value: `$${(supplier.totalSourcing12m / 1000).toFixed(0)}K` },
      { label: 'Overall Efficiency', value: `${supplier.overallEfficiency}%` },
    ], y);

    if (supplier.riskNewsList.length) {
      y = addSectionTitle(doc, 'Risk News', y);
      supplier.riskNewsList.forEach(n => { y = addText(doc, `[${n.severity}] ${n.title} (${n.date})`, y, { fontSize: 9 }); y += 2; });
    }

    y = checkNewPage(doc, y, 80);
    y = addSectionTitle(doc, 'Component-wise Analysis', y);
    y = addTable(doc,
      ['MPN', 'BOM', 'Your Price', 'Best Rate', 'Anchor', 'Eff%', 'Efficacy%', 'Risk'],
      supplier.components.map(c => [c.mpn, c.bomRef, `$${c.unitPrice.toFixed(3)}`, `$${c.bestRate.toFixed(3)}`, `$${c.anchor.toFixed(3)}`, `${c.efficiency}%`, `${c.efficacy}%`, c.risk]),
      y
    );

    y = checkNewPage(doc, y, 80);
    y = addSectionTitle(doc, 'Negotiation Talking Points', y);
    const points = [
      `1. Total sourcing: $${(supplier.totalSourcing12m / 1000).toFixed(0)}K in 12 months. Volume justifies tier pricing.`,
      `2. Overall efficiency at ${supplier.overallEfficiency}%. ${supplier.overallEfficiency < 70 ? 'Significant room for improvement — present competitive quotes.' : 'Competitive, but specific MPNs can be improved.'}`,
    ];
    const weak = supplier.components.filter(c => c.efficiency < 75);
    if (weak.length) points.push(`3. ${weak.length} component(s) below 75% efficiency. Lead with ${weak[0].mpn}: your $${weak[0].unitPrice.toFixed(3)} vs anchor $${weak[0].anchor.toFixed(3)}.`);
    if (supplier.riskNewsList.length) points.push(`4. ${supplier.riskNewsList.length} risk alert(s). Use as leverage: reliability concerns support price concession requests.`);
    points.push(`5. Propose annual commitment at $${(supplier.totalSourcing12m * 1.1 / 1000).toFixed(0)}K (10% growth) for volume discounts.`);
    points.forEach(p => { y = addText(doc, p, y, { fontSize: 9 }); y += 2; });

    downloadPDF(doc, `negotiation-kit-${supplier.name.replace(/\s+/g, '-').toLowerCase()}.pdf`);
    toast.success(`Negotiation Kit for ${supplier.name} downloaded`);
  };

  /* Detail View */
  if (selectedSupplier) {
    const s = selectedSupplier;
    return (
      <Layout title="Supplier Improvements" subtitle="Live BOM supplier analysis">
        <div className="space-y-6" data-testid="supplier-detail-page">
          <Button variant="ghost" onClick={() => setSelectedSupplier(null)} className="text-gray-600 -mb-2" data-testid="back-to-list">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Supplier List
          </Button>

          {/* Supplier Header */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{s.name}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline">{s.region}</Badge>
                    <Badge className={effBadge(s.overallEfficiency)}>Efficiency: {s.overallEfficiency}%</Badge>
                    {s.hasRiskNews && <Badge className="bg-red-100 text-red-800"><AlertTriangle className="h-3 w-3 mr-1" />{s.riskNewsList.length} Risk Alert(s)</Badge>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => generateNegotiationKit(s)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="detail-neg-kit">
                    <Briefcase className="h-4 w-4 mr-2" /> Negotiation Kit
                  </Button>
                  <Button variant="outline" className="border-green-300 text-green-700" onClick={() => generateNegotiationKit(s)} data-testid="detail-export-pdf">
                    <Download className="h-4 w-4 mr-2" /> Export PDF
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Total Sourcing (12m)</p>
                  <p className="text-xl font-bold text-gray-900">${(s.totalSourcing12m / 1000).toFixed(0)}K</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Components Supplied</p>
                  <p className="text-xl font-bold text-gray-900">{s.components.length}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Avg Efficacy</p>
                  <p className="text-xl font-bold text-gray-900">{Math.round(s.components.reduce((a, c) => a + c.efficacy, 0) / s.components.length)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Risk News */}
          {s.riskNewsList.length > 0 && (
            <Card className="bg-red-50 border-red-200 shadow-sm" data-testid="supplier-risk-news">
              <CardHeader className="pb-2">
                <CardTitle className="text-red-800 flex items-center gap-2 text-base">
                  <Newspaper className="h-5 w-5" /> News & Risk Alerts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {s.riskNewsList.map((n, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-red-100">
                      <Badge className={n.severity === 'High' ? 'bg-red-100 text-red-800' : n.severity === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}>{n.severity}</Badge>
                      <div>
                        <p className="text-sm text-gray-900 font-medium">{n.title}</p>
                        <p className="text-xs text-gray-500">{n.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Component-wise Analysis */}
          <Card className="bg-white border-gray-200 shadow-sm" data-testid="supplier-components-table">
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-2">
                Component-wise Efficiency & Efficacy
                <InfoTip text="Efficiency = How close your price is to the best available rate. Efficacy = How effective the supplier is at delivering value (price + reliability + availability combined). BOM Ref shows which Live BOM the component belongs to." />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>MPN</TableHead>
                    <TableHead>BOM Reference</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Your Price</TableHead>
                    <TableHead>Best Rate</TableHead>
                    <TableHead>Anchor</TableHead>
                    <TableHead>Efficiency</TableHead>
                    <TableHead>Efficacy</TableHead>
                    <TableHead>Risk</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {s.components.map((c, idx) => (
                    <TableRow key={idx} data-testid={`comp-row-${idx}`}>
                      <TableCell className="font-mono font-medium text-sm">{c.mpn}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{c.bomRef}</Badge></TableCell>
                      <TableCell className="text-sm text-gray-600">{c.category}</TableCell>
                      <TableCell className="font-medium">${c.unitPrice.toFixed(3)}</TableCell>
                      <TableCell className="text-green-700 font-semibold">${c.bestRate.toFixed(3)}</TableCell>
                      <TableCell className="text-blue-700 font-semibold">${c.anchor.toFixed(3)}</TableCell>
                      <TableCell><Badge className={effBadge(c.efficiency)}>{c.efficiency}%</Badge></TableCell>
                      <TableCell><Badge className={effBadge(c.efficacy)}>{c.efficacy}%</Badge></TableCell>
                      <TableCell><Badge className={c.risk === 'Low' ? 'bg-green-100 text-green-800' : c.risk === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>{c.risk}</Badge></TableCell>
                      <TableCell><QuickActions mpn={c.mpn} bomRef={c.bomRef} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Price Trends per Component */}
          <Card className="bg-white border-gray-200 shadow-sm" data-testid="supplier-price-trends">
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-600" /> Price Trends
                <InfoTip text="30-day forward price forecast for components supplied by this distributor." />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {s.components.map((c, idx) => (
                  <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono font-semibold text-sm">{c.mpn}</span>
                      <Badge className={c.trend > 0 ? 'bg-red-100 text-red-800' : c.trend < 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                        {c.trend > 0 ? '+' : ''}{c.trend}%
                      </Badge>
                    </div>
                    <p className="text-[10px] text-gray-500 mb-1">30-Day Price Forecast (USD)</p>
                    <ResponsiveContainer width="100%" height={90}>
                      <AreaChart data={c.trendData}>
                        <Area type="monotone" dataKey="price" stroke={c.trend > 0 ? '#ef4444' : '#16a34a'} fill={c.trend > 0 ? '#fee2e2' : '#dcfce7'} strokeWidth={2} />
                        <XAxis dataKey="day" tick={{ fontSize: 8 }} label={{ value: 'Day', position: 'insideBottomRight', offset: -2, fontSize: 9, fill: '#9ca3af' }} />
                        <YAxis domain={['auto', 'auto']} tick={{ fontSize: 9 }} width={40} label={{ value: 'Price ($)', angle: -90, position: 'insideLeft', offset: 10, fontSize: 9, fill: '#9ca3af' }} />
                        <Tooltip formatter={v => [`$${Number(v).toFixed(3)}`, 'Price']} labelFormatter={d => `Day ${d}`} />
                      </AreaChart>
                    </ResponsiveContainer>
                    <p className="text-xs text-gray-400 text-center"><Badge variant="outline" className="text-[10px]">{c.bomRef}</Badge></p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  /* ——— LIST VIEW ——— */
  return (
    <Layout title="Supplier Improvements" subtitle="Live BOM supplier analysis — efficiency, risks, and negotiation readiness">
      <div className="space-y-6" data-testid="supplier-improvements-page">
        {/* Analytics Header */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-5">
              <p className="text-xs text-gray-500 uppercase flex items-center">Total Suppliers <InfoTip text="Distinct suppliers across all Live BOMs in last 12 months." /></p>
              <p className="text-2xl font-bold text-gray-900">{suppliersData.length}</p>
              <p className="text-xs text-gray-400 mt-1">Across Live BOMs</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200 shadow-sm" data-testid="risk-news-kpi">
            <CardContent className="pt-5">
              <p className="text-xs text-red-600 uppercase flex items-center"><ShieldAlert className="h-3 w-3 mr-1" /> Suppliers with Risk News</p>
              <p className="text-2xl font-bold text-red-700">{suppliersWithRisk}</p>
              <p className="text-xs text-red-400 mt-1">Require attention</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200 shadow-sm" data-testid="low-eff-kpi">
            <CardContent className="pt-5">
              <p className="text-xs text-amber-600 uppercase flex items-center"><AlertTriangle className="h-3 w-3 mr-1" /> Efficiency &lt; 50% (${(lowEffThreshold / 1000).toFixed(0)}K+)</p>
              <p className="text-2xl font-bold text-amber-700">{suppliersLowEff}</p>
              <p className="text-xs text-amber-400 mt-1">For $100K+ or &gt;5% of total sourcing</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex gap-3 items-center flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input placeholder="Search supplier or component MPN..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="supplier-search" />
              </div>
              <Select value={bomFilter} onValueChange={setBomFilter}>
                <SelectTrigger className="w-52 h-9" data-testid="bom-filter">
                  <SelectValue placeholder="BOM" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Live BOMs</SelectItem>
                  {allBoms.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={countryFilter} onValueChange={setCountryFilter}>
                <SelectTrigger className="w-36 h-9" data-testid="country-filter">
                  <SelectValue placeholder="Region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  {allRegions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <DateRangeSelect value={dateRange} onChange={setDateRange} testId="date-range-filter" />
              <Input type="number" placeholder="Min sourcing value ($)" value={minValue} onChange={e => setMinValue(e.target.value)} className="w-48 h-9" data-testid="min-value-filter" />
            </div>
          </CardContent>
        </Card>

        {/* Supplier List Table */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-gray-900 flex items-center gap-2">
              <Truck className="h-5 w-5 text-green-600" /> Supplier List
              <Badge variant="outline" className="ml-2">{filtered.length} suppliers</Badge>
              <InfoTip text="Only suppliers from Live BOMs are shown. Efficiency is computed as best-rate / your-price across all components. Efficacy combines price competitiveness with reliability." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Supplier</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>Total Sourcing (12m)</TableHead>
                  <TableHead>Efficiency</TableHead>
                  <TableHead>Efficacy</TableHead>
                  <TableHead>Components</TableHead>
                  <TableHead>Risk News</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(s => {
                  const avgEfficacy = Math.round(s.components.reduce((a, c) => a + c.efficacy, 0) / s.components.length);
                  return (
                    <TableRow key={s.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedSupplier(s)} data-testid={`supplier-row-${s.id}`}>
                      <TableCell className="font-semibold text-gray-900">{s.name}</TableCell>
                      <TableCell><Badge variant="outline">{s.region}</Badge></TableCell>
                      <TableCell className="font-medium">${(s.totalSourcing12m / 1000).toFixed(0)}K</TableCell>
                      <TableCell><Badge className={effBadge(s.overallEfficiency)}>{s.overallEfficiency}%</Badge></TableCell>
                      <TableCell><Badge className={effBadge(avgEfficacy)}>{avgEfficacy}%</Badge></TableCell>
                      <TableCell>{s.components.length}</TableCell>
                      <TableCell>
                        {s.hasRiskNews ? (
                          <Badge className="bg-red-100 text-red-800"><AlertTriangle className="h-3 w-3 mr-1" />{s.riskNewsList.length}</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800">None</Badge>
                        )}
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setSelectedSupplier(s)} data-testid={`view-detail-${s.id}`}>
                            Detail <ExternalLink className="h-3 w-3 ml-1" />
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-green-700 border-green-200" onClick={() => generateNegotiationKit(s)} data-testid={`neg-kit-${s.id}`}>
                            <Briefcase className="h-3 w-3 mr-1" /> Kit
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
      <QuickAssignModal open={assignOpen} onClose={() => setAssignOpen(false)} context={assignCtx} />
    </Layout>
  );
};

export default SupplierScorecard;
